'use client';

import React, { useEffect, useState } from 'react';
import styles from './DocumentSettingsModal.module.scss';
import { gql } from '@apollo/client';
import {
	useDocumentSettingsModal_GetDocumentWhereQuery,
	useDocumentSettingsModal_ShareDocumentMutation,
	useDocumentSettingsModal_UpdateDocumentMutation,
} from '#types';
import MuiModal from '../../mui/MuiModal/MuiModal';
import { Box } from '@mui/material';
import { Button } from '../Button';
import { DocumentSettingsModalShareList } from './DocumentSettingsModalShareList';
import { Spinner } from '../Spinner';
import { DocumentSettingsModalExportableCheckbox } from './DocumentSettingsModalExportableCheckbox';
import {
	useDocumentSettingsModal,
	useDocumentSettingsModalDocumentFragment,
	UseDocumentSettingsModalProps,
} from './useDocumentSettingsModal';

const documentSettingsModalDocumentFragment = gql`
	fragment documentSettingsModalDocument on Document {
		id
		name
		exportable
		...documentSettingsModalShareListCustomDocumentWithDownloadUrl
		...useDocumentSettingsModalDocument
	}
	${ useDocumentSettingsModalDocumentFragment }
	${ DocumentSettingsModalShareList.fragments
		.documentSettingsModalShareListCustomDocumentWithDownloadUrlFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DocumentSettingsModal_getDocumentWhere = gql`
	query DocumentSettingsModal_getDocumentWhere(
		$where: DocumentWhereUniqueInput!
	) {
		getDocumentWhere(where: $where) {
			...documentSettingsModalDocument
		}
	}
	${ documentSettingsModalDocumentFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DocumentSettingsModal_shareDocument = gql`
	mutation DocumentSettingsModal_shareDocument(
		$where: DocumentWhereUniqueInput!
		$data: CustomShareDocumentInput!
	) {
		shareDocument(where: $where, data: $data) {
			...documentSettingsModalDocument
		}
	}
	${ documentSettingsModalDocumentFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DocumentSettingsModal_updateDocument = gql`
	mutation DocumentSettingsModal_updateDocument(
		$where: DocumentWhereUniqueInput!
		$data: UpdateDocumentInput!
	) {
		updateDocument(where: $where, data: $data) {
			...documentSettingsModalDocument
		}
	}
	${ documentSettingsModalDocumentFragment }
`;

export type DocumentSettingsModalProps = {
	customerId?: string;
	open?: boolean;
	onClose?: () => void;
} & Pick<UseDocumentSettingsModalProps, 'documentId' | 'onDocumentShared'>;

export const DocumentSettingsModal: React.FC<DocumentSettingsModalProps> = ( {
	open: openProp,
	onClose,
	documentId,
	customerId,
	onDocumentShared,
} ) => {
	const [ open, setOpen ] = useState( false );

	useEffect( () => {
		// if open is specified, then use it as the state
		if ( typeof openProp === 'boolean' ) {
			setOpen( openProp );
		} else {
			// if open is not specified, then open the modal by default as it's in ModalContext
			setOpen( true );
		}
	}, [ openProp ] );

	const {
		document,
		isOwner,
		exportable,
		setExportable,
		setSharedWithContacts,
		setSharedWithCustomer,
		sharedWithContacts,
		sharedWithCustomer,
		handleClose,
		handleSave,
		loading,
	} = useDocumentSettingsModal( {
		documentId,
		onDocumentShared,
		useDocumentSettingsModal_ShareDocumentMutation,
		useDocumentSettingsModal_UpdateDocumentMutation,
		useDocumentSettingsModal_GetDocumentWhereQuery,
		onClose,
	} );
	return (
		<MuiModal
			muiModalProps={ {
				open,
				onClose: handleClose,
				className: styles.container,
				slotProps: { paper: { [ 'data-cy' ]: 'share-document-modal' } },
			} }
			title='File settings'
			description={
				isOwner
					? undefined
					: 'Only the document\'s owner can change the file settings for a document.'
			}
			controls={
				document ? (
					<Box className={ styles.controls }>
						{ !isOwner ? (
							<Button onClick={ ( e ) => handleClose( e, 'fromCode' ) }>
								Got it
							</Button>
						) : (
							<>
								<Button
									variant='outlined'
									onClick={ ( e ) => handleClose( e, 'fromCode' ) }
								>
									Cancel
								</Button>
								<Button color='primary' loading={ loading } onClick={ handleSave }>
									Save
								</Button>
							</>
						) }
					</Box>
				) : undefined
			}
		>
			{ ' ' }
			{ !document ? (
				<Box className={ styles.spinner }>
					<Spinner />
				</Box>
			) : (
				<>
					<DocumentSettingsModalExportableCheckbox
						isOwner={ isOwner }
						checked={ exportable }
						setChecked={ setExportable }
					/>
					<DocumentSettingsModalShareList
						customerId={ customerId }
						document={ document }
						setSharedWithContacts={ setSharedWithContacts }
						sharedWithContacts={ sharedWithContacts }
						setSharedWithCustomer={ setSharedWithCustomer }
						sharedWithCustomer={ sharedWithCustomer }
						isOwner={ isOwner }
					/>
				</>
			) }
		</MuiModal>
	);
};
